import React, { useState, useRef, useEffect } from "react";
import {
  TabsContainer,
  TabsWrapper,
  TabItem,
  InkBar,
  TabDot,
} from "./Tabs.styles";
import { TabsProps } from "./Tabs.types";
import { Skeleton } from "@mui/material";
import { Body5MediumTypography } from "../../primitives/Typography/typographyVariants";

export const Tabs: React.FC<TabsProps> = ({
  tabs,
  selectedTabId,
  onTabChange,
  isLoading = false,
  skipFirstTab = false,
}) => {
  const getSelectedIndex = (tabId: number) => {
    let index = tabs.findIndex((tab) => tab.id === tabId);
    return index !== -1 ? index : 0;
  };
  const [inkBarStyle, setInkBarStyle] = useState({ left: 0, width: 0 });
  const tabRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const [selectedTabIndex, setSelectedTabIndex] = useState(
    getSelectedIndex(selectedTabId)
  );

  // Handle tab change
  const changeTab = (index: number) => {
    if (index >= 0 && index < tabs.length) {
      setSelectedTabIndex(index);
      onTabChange(tabs[index].id);
    }
  };

  // Update InkBar position whenever tab or loading state changes
  useEffect(() => {
    const activeTab = tabRefs.current[selectedTabIndex];
    if (activeTab) {
      setInkBarStyle({
        left: activeTab.offsetLeft,
        width: activeTab.offsetWidth,
      });
    }
  }, [selectedTabIndex, isLoading, tabs]); // Include `tabs` in dependency array to trigger recalculation

  useEffect(() => {
    const newIndex = getSelectedIndex(selectedTabId);
    if (newIndex !== -1 && newIndex !== selectedTabIndex) {
      setSelectedTabIndex(newIndex);
    }
  }, [selectedTabId, tabs]);

  return (
    <>
      {/* Tabs Navigation */}
      <TabsContainer>
        <TabsWrapper>
          {tabs.map((tab, index) => (
            <TabItem
              key={tab.id}
              ref={(el: any) => (tabRefs.current[index] = el)}
              isActive={selectedTabIndex === index}
              onClick={() => changeTab(index)}
              disabled={!tab?.clickable}
              data-cy={tab?.dataCy}
              data-selected={selectedTabIndex === index ? "true" : "false"}
            >
              {isLoading ? (
                <Skeleton width="80px" height="18px" />
              ) : (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  {tab?.showDot && tab?.dotColor && (
                    <TabDot color={tab.dotColor} />
                  )}
                  <Body5MediumTypography textAlign="center">
                    {tab?.showPillNumber
                      ? `${tab?.name} ${isLoading ? "" : `(${tab?.number})`}`
                      : tab?.label}
                  </Body5MediumTypography>
                </div>
              )}
            </TabItem>
          ))}
          <InkBar style={inkBarStyle} />
        </TabsWrapper>
      </TabsContainer>
    </>
  );
};
